import { useCallback, useState } from 'react';
import { t } from '@lingui/macro';

import {
  type SearchParamsPetCounts,
  SearchParamsPetType,
} from '@rover/constants/src/translations/sitterProfilePage.constants';
import { Box, Flex, Heading } from '@rover/kibble/core';
import { EnhancedPersonSitterProfile } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfile';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import { useCurrentUser } from '@rover/rsdk/src/modules/Data/Core/CurrentUser';
import { isMobileBrowser } from '@rover/rsdk/src/modules/Network/userAgent';
import useBrandSpecificPets from '@rover/shared/js/hooks/useBrandSpecificPets';
import useIsBHUser from '@rover/shared/js/hooks/useIsBHUser';
import type { PriceTransparencyLevel } from '@rover/types/src/PriceTransparencyLevel';
import { PriceTransparencyLevels } from '@rover/types/src/PriceTransparencyLevel';
import { filterServicesBasedOnPetTypes } from '@rover/utilities/getAvailablePetTypes/availablePetTypesUtils';
import { getIsPriceTransparencyCondensed } from '@rover/utilities/priceTransparency';

import FeeExplanationBottomSheet from '../../../../search/SearchPage/components/BottomSheets/FeeExplanationBottomSheet';
import { FeeExplanationModal } from '../../../../search/SearchPage/components/Modals/FeeExplanationModal';
import {
  PtCondensedBanner,
  PtCondensedPetTypeDescription,
} from '../../../../search/SearchPage/components/PtCondensedBanner';
import { SitterProfileBHPriceToggleClickEvent } from '../../analyticsEvents';
import { isSitterInPetCareModesExperiment } from '../../hooks/isSitterInPetCareModesExperiment';
import { groupServicesByLocationType, usePrimaryPetType } from '../../utils/utils';

import AdditionalRatesSection from './AdditionalRatesSection';
import Separator from './Separator';
import ServicePreferencesSection, { petPreferencesTypes } from './ServicePreferencesSection';
import ServiceSectionItem from './ServiceSectionItem';
import SittersRatesToggle from './SittersRatesToggle';

type Props = {
  sitterProfile: EnhancedPersonSitterProfile;
  petType?: SearchParamsPetType;
  petCounts: SearchParamsPetCounts;
  selectedDate?: Date;
  selectedEndDate?: Date;
  priceTransparencyLevel?: PriceTransparencyLevel;
  hasDateRange?: boolean;
  isShowingPriceInCurrency?: boolean;
  bookingFeePercentage?: string;
  isRevampVersion?: boolean;
  setNumberOfServices?: (num: number) => void; // only used for revamp version to set the services to render in the accordion section
};

const ServiceSection = ({
  sitterProfile,
  petType,
  petCounts,
  selectedDate,
  selectedEndDate,
  priceTransparencyLevel,
  hasDateRange,
  isShowingPriceInCurrency,
  bookingFeePercentage,
  isRevampVersion = false,
  setNumberOfServices, // only used for revamp version to set the services to render in the accordion section
}: Props): JSX.Element => {
  const { services: sitterServices, opk, authenticatedContext } = sitterProfile;
  const isPetCareModes = isSitterInPetCareModesExperiment(sitterProfile);
  const isBHUser = useIsBHUser();
  const { user } = useCurrentUser();
  const brandPets = useBrandSpecificPets();
  const [isFeeExplanationModalOpen, setIsFeeExplanationModalOpen] = useState(false);
  const source = isMobileBrowser() ? 'mobile_web' : 'web';

  const openFeeExplanationModal = (): void => {
    setIsFeeExplanationModalOpen(true);
  };

  const availableServices = filterServicesBasedOnPetTypes(sitterServices, brandPets);
  const { hostingActiveServices, travelingActiveServices } =
    groupServicesByLocationType(availableServices);

  const servicesToRender = [
    ...availableServices.filter((s) => !s.isAway),
    ...availableServices.filter((s) => s.isAway),
  ];
  const [showSittersRates, setShowSittersRates] = useState(isShowingPriceInCurrency ?? false);

  setNumberOfServices?.(servicesToRender.length);

  const shouldDisplaySittersRates = authenticatedContext?.shouldShowBhToggle
    ? showSittersRates
    : true;

  const handleSittersRatesToggle = useCallback(
    (checked: boolean): void => {
      user?.isBrightHorizonsUser &&
        emitAnalyticsEvent(
          new SitterProfileBHPriceToggleClickEvent({
            personId: user.opk,
          })
        );
      setShowSittersRates(checked);
    },
    [user]
  );

  const displayPetType = usePrimaryPetType(petCounts, petType, sitterServices);
  const isPtCondensed = getIsPriceTransparencyCondensed(priceTransparencyLevel);

  return (
    <Flex flexDirection="column" width="100%" data-testid="service-section">
      {!isRevampVersion && (
        <Heading webProps={{ responsive: false }} size="400" mb="0x">
          {t`Services`}
        </Heading>
      )}
      {isPtCondensed && (
        <Box mt="1x">
          <Box mb="3x">
            <PtCondensedPetTypeDescription petType={displayPetType} />
          </Box>
          <PtCondensedBanner
            priceTransparencyLevel={priceTransparencyLevel}
            bookingFeePercentage={bookingFeePercentage}
            onClick={openFeeExplanationModal}
            isSitterProfile
          />
        </Box>
      )}
      <Flex flexDirection="column">
        {isBHUser && authenticatedContext?.shouldShowBhToggle && (
          <SittersRatesToggle onChange={handleSittersRatesToggle} value={showSittersRates} />
        )}
        {servicesToRender.map((ser, idx) => (
          <ServiceSectionItem
            key={ser.slug}
            index={idx}
            service={ser}
            petType={petType}
            petCounts={petCounts}
            selectedDate={selectedDate}
            selectedEndDate={selectedEndDate}
            hasDateRange={hasDateRange}
            sitterProfileOpk={opk}
            showSittersRates={shouldDisplaySittersRates}
          />
        ))}
        <Separator withBorder={false} />
        <AdditionalRatesSection
          services={availableServices}
          petType={petType}
          showSittersRates={shouldDisplaySittersRates}
          isSitterInPetCareModesExperiment={isPetCareModes}
          isRevampVersion={isRevampVersion}
        />
        {!isRevampVersion && (
          <Box pb="10x">
            {sitterProfile.providerProfile?.hostingServicePreferences && (
              <ServicePreferencesSection
                sitterName={sitterProfile.firstName}
                preferences={sitterProfile.providerProfile?.hostingServicePreferences}
                preferencesType={petPreferencesTypes.HOSTING}
                activeServices={hostingActiveServices}
                weightLabel={sitterProfile.countryWeightLabel}
                dogSizes={sitterProfile.countryDogSizes}
              />
            )}
            {sitterProfile.providerProfile?.travelingServicePreferences && (
              <ServicePreferencesSection
                sitterName={sitterProfile.firstName}
                preferences={sitterProfile.providerProfile?.travelingServicePreferences}
                preferencesType={petPreferencesTypes.TRAVELING}
                activeServices={travelingActiveServices}
                weightLabel={sitterProfile.countryWeightLabel}
                dogSizes={sitterProfile.countryDogSizes}
              />
            )}
          </Box>
        )}
      </Flex>
      {source === 'mobile_web'
        ? bookingFeePercentage && (
            <FeeExplanationBottomSheet
              isOpen={isFeeExplanationModalOpen}
              onClose={() => setIsFeeExplanationModalOpen(false)}
              priceTransparencyLevel={
                priceTransparencyLevel || PriceTransparencyLevels.lite_condensed
              }
              bookingPercentageFee={bookingFeePercentage}
            />
          )
        : bookingFeePercentage && (
            <FeeExplanationModal
              isOpen={isFeeExplanationModalOpen}
              onClose={() => setIsFeeExplanationModalOpen(false)}
              priceTransparencyLevel={
                priceTransparencyLevel || PriceTransparencyLevels.lite_condensed
              }
              bookingPercentageFee={bookingFeePercentage}
            />
          )}
    </Flex>
  );
};

export default ServiceSection;
